// ** MUI Imports
import Grid from '@mui/material/Grid'
import Table from '@mui/material/Table'
import Divider from '@mui/material/Divider'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import {styled, useTheme} from '@mui/material/styles'
import TableContainer from '@mui/material/TableContainer'
import TableCell, {TableCellBaseProps} from '@mui/material/TableCell'

// ** Configs
import themeConfig from 'src/configs/themeConfig'

// ** Types
import {OrderViewDTO} from "../types/dto";
import {OrderFields} from "../types/fields";
import {EntityWithIdFields} from "../../../types";
import {dateFormatter} from "../../../utils/formatters";
import OrderStatusChip from "./OrderStatusChip";
import React from 'react'
import {grey} from "@mui/material/colors";
import OrderStatusTimeline from "../../../components/timeline/OrderStatusTimeline";

interface Props {
    order: OrderViewDTO
}

const MUITableCell = styled(TableCell)<TableCellBaseProps>(({theme}) => ({
    borderBottom: 0,
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
    paddingTop: `${theme.spacing(1)} !important`,
    paddingBottom: `${theme.spacing(1)} !important`
}))


const OrderDetailCard = ({order}: Props) => {
    // ** Hook
    const theme = useTheme()

    if (order) {
        return (
            <>
                <Grid container spacing={2}>


                    <Grid item sm={6} xs={12} sx={{mb: {sm: 0, xs: 4}}}>
                        <Box sx={{display: 'flex', flexDirection: 'column'}}>
                            <Box sx={{mb: 6, display: 'flex', alignItems: 'center'}}>
                                <img
                                    src={`/images/logo_nueva_italia.svg`}
                                    height={'40'}
                                    alt={`nueva-italia-logo`}
                                />
                                <Typography
                                    variant='h6'
                                    sx={{ml: 2.5, fontWeight: 600, lineHeight: 'normal', textTransform: 'uppercase'}}
                                >
                                    {themeConfig.templateName}
                                </Typography>
                            </Box>

                        </Box>
                    </Grid>

                    <Grid item sm={6} xs={12}>
                        <Box sx={{display: 'flex', justifyContent: {xs: 'flex-start', sm: 'flex-end'}}}>
                            <Table sx={{maxWidth: '200px'}}>
                                <TableBody>
                                    <TableRow>
                                        <MUITableCell>
                                            <Typography variant='h6'>Entrega</Typography>
                                        </MUITableCell>
                                        <MUITableCell>
                                            <Typography variant='h6'>{`#${order[EntityWithIdFields.Id]}`}</Typography>
                                        </MUITableCell>
                                    </TableRow>
                                    <TableRow>
                                        <MUITableCell>
                                            <Typography variant='body2'>Fecha creación:</Typography>
                                        </MUITableCell>
                                        <MUITableCell>
                                            <Typography variant='body2' sx={{fontWeight: 600}}>
                                                {dateFormatter.toShortDate(order[OrderFields.OrderDate])}
                                            </Typography>
                                        </MUITableCell>
                                    </TableRow>
                                    <TableRow>
                                        <MUITableCell>
                                            <Typography variant='body2'>Estado:</Typography>
                                        </MUITableCell>
                                        <MUITableCell>
                                            <OrderStatusChip orderOption={order}/>
                                        </MUITableCell>
                                    </TableRow>
                                    <TableRow>
                                        <MUITableCell>
                                            <Typography variant='body2'>Remito:</Typography>
                                        </MUITableCell>
                                        <MUITableCell>
                                            <Typography variant='body2' sx={{fontWeight: 600}}>
                                                {order?.[OrderFields.InvoiceNumber] || '-'}
                                            </Typography>
                                        </MUITableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Grid>
                </Grid>


                <Divider/>


                <Grid container>
                    <Grid item xs={12} sm={6} sx={{mb: {lg: 0, xs: 4}}}>
                        <Typography
                            sx={{flex: '1 1 100%', fontSize: '1rem', fontWeight: 600}}
                            color="inherit"
                            component="div"
                        >
                            Materiales
                        </Typography>
                        <TableContainer>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell size="small" sx={{px: 1, py: 1}}>Cantidad</TableCell>
                                        <TableCell size="small" sx={{px: 1, py: 1}}>Descripción</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {order[OrderFields.Items].split('\n').map((item, index) => {
                                        const [quantity, ...descriptionParts] = item.split(' ');
                                        const description = descriptionParts.join(' ');

                                        return (
                                            <TableRow key={index}
                                                      sx={{backgroundColor: index % 2 === 0 ? grey[200] : 'inherit'}}
                                            >
                                                <TableCell size="small" sx={{px: 1, py: 1}}>{quantity}</TableCell>
                                                <TableCell size="small" sx={{px: 1, py: 1}}>{description}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    <Grid item xs={12} sm={6} sx={{mb: {lg: 0, xs: 4}}}>
                        <Typography
                            sx={{flex: '1 1 100%', fontSize: '1rem', fontWeight: 600}}
                            color="inherit"
                            component="div"
                        >
                            Devoluciones
                        </Typography>
                        <TableContainer>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell size="small" sx={{px: 1, py: 1}}>Cantidad</TableCell>
                                        <TableCell size="small" sx={{px: 1, py: 1}}>Descripción</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {order[OrderFields.Observations]?.split('\n').map((item, index) => {
                                        const [quantity, ...descriptionParts] = item.split(' ');
                                        const description = descriptionParts.join(' ');

                                        return (
                                            <TableRow key={index}
                                                      sx={{backgroundColor: index % 2 !== 0 ? grey[200] : 'inherit'}}
                                            >
                                                <TableCell size="small" sx={{px: 1, py: 1}}>{quantity}</TableCell>
                                                <TableCell size="small" sx={{px: 1, py: 1}}>{description}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                    </Grid>


                    <Grid item xs={12} sx={{mt: 1}}>

                        <OrderStatusTimeline orderId={order[EntityWithIdFields.Id]}/>
                    </Grid>
                </Grid>


            </>
        )
    } else {
        return null
    }
}

export default OrderDetailCard
